// ADR 16.9.26r §2 — where the dock's state lives, and why it is a context.
//
// The cell that renders the dock is the shell's (`layout/ShellBody.tsx`), and
// a page cannot open a cell it does not own — 8.9.26i §4's argument, 16.9.26n
// §2's again. So the FAB, the dock cell, the dock's own `TutorPanel` and
// 16.9.26t's coach card all read **one** instance, mounted by `AdaptiveFrame`
// beside `TutorContextProvider`. The transitions themselves are
// `dockState.ts`'s and pure; this file is the React half — the state, the
// pending seed, the emit, and the one focus hand-off the dock cannot make
// through a ref (the FAB's ref is null while the dock is open).

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'

import { capture } from '../../../telemetry/capture'
import type { SurfaceHost } from '../../companion/hosts'
import { DOCK_TOGGLED_EVENT, dockToggledProps } from './dockTelemetry'
import {
  bye as byeState,
  closed,
  dockScreen,
  hello as helloState,
  opened,
  readDockState,
  returned,
  writeDockState,
  type DockMemory,
  type DockState,
} from './dockState'
import { holdsPopout, openPopout, returnToPopout } from './popout'

export interface TutorDock {
  state: DockState
  open(): void
  close(): void
  /** 16.9.26t's door: open the dock and send `text` as the first turn (`coach`). */
  openSeeded(text: string): void
  /** §3 — the header's pop-out control; a blocked popup leaves the state alone. */
  popOut(): void
  /** §3 — the FAB's return press: focus the popup, or fold back to `open` if it died. */
  returnToPopout(): void
  /** The channel's two signals, applied by `TutorContextProvider`. */
  hello(): void
  bye(): void
  /** Non-null while a seed waits for the dock's panel to have a session. */
  pendingSeed: string | null
  /** Counts `openSeeded` calls, so the same text seeded twice is two sends. */
  seedTicket: number
  /** Clears the seed synchronously and returns it — once, StrictMode or not. */
  takeSeed(): string | null
  /** True once, after a close: the re-mounted FAB takes focus. */
  takeFabFocus(): boolean
}

const NOOP_DOCK: TutorDock = {
  state: 'closed',
  open: () => {},
  close: () => {},
  openSeeded: () => {},
  popOut: () => {},
  returnToPopout: () => {},
  hello: () => {},
  bye: () => {},
  pendingSeed: null,
  seedTicket: 0,
  takeSeed: () => null,
  takeFabFocus: () => false,
}

/** Defaulted, not throwing: `TutorRoute` is rendered without the shell in tests. */
const TutorDockContext = createContext<TutorDock>(NOOP_DOCK)

export function useTutorDock(): TutorDock {
  return useContext(TutorDockContext)
}

/**
 * One transition: apply, persist, and emit — `hello`/`bye` only where the
 * proxy is. Computed against a ref and **outside** the updater: updaters must
 * be pure (StrictMode double-invokes them — `useDueCards.ts:19–39`), and an
 * emit inside one would count twice in development.
 */
function useDockMemory(surface: ReturnType<typeof dockScreen>, host: SurfaceHost) {
  const [memory, setMemory] = useState<DockMemory>(readDockState)
  const memoryRef = useRef(memory)
  const transition = useCallback(
    (step: (memory: DockMemory) => DockMemory, counted: () => boolean = () => true) => {
      const current = memoryRef.current
      const next = step(current)
      if (next.state === current.state) return
      memoryRef.current = next
      writeDockState(next)
      if (counted()) capture(DOCK_TOGGLED_EVENT, dockToggledProps(next.state, surface ?? 'other', host))
      setMemory(next)
    },
    [surface, host],
  )
  return { memory, transition }
}

/**
 * ADR 7.10.26w §6/§7 — `host` comes from `CompanionProviders`, which reads the
 * spread; it defaults to `sheet` for the suites that mount this alone.
 */
export function TutorDockProvider({ host = 'sheet', children }: { host?: SurfaceHost; children: ReactNode }) {
  const { pathname } = useLocation()
  const { memory, transition } = useDockMemory(dockScreen(pathname), host)
  const [seed, setSeed] = useState<{ text: string | null; ticket: number }>({ text: null, ticket: 0 })
  // The ref is the seed's truth (`takeSeed` empties it synchronously, so a
  // StrictMode second run finds nothing); the state is the consumer's trigger.
  const seedRef = useRef<string | null>(null)
  const fabFocusRef = useRef(false)

  const value = useMemo<TutorDock>(
    () => ({
      state: memory.state,
      pendingSeed: seed.text,
      seedTicket: seed.ticket,
      open: () => transition(opened),
      close: () => {
        fabFocusRef.current = true
        transition(closed)
      },
      openSeeded: (text) => {
        seedRef.current = text
        setSeed((current) => ({ text, ticket: current.ticket + 1 }))
        transition(opened)
      },
      popOut: () => {
        openPopout()
      },
      returnToPopout: () => {
        if (returnToPopout()) transition(returned)
      },
      hello: () => transition(helloState, holdsPopout),
      bye: () => transition(byeState, holdsPopout),
      takeSeed: () => {
        const text = seedRef.current
        seedRef.current = null
        if (text !== null) setSeed((current) => ({ text: null, ticket: current.ticket }))
        return text
      },
      takeFabFocus: () => {
        const wanted = fabFocusRef.current
        fabFocusRef.current = false
        return wanted
      },
    }),
    [memory.state, seed, transition],
  )

  return <TutorDockContext.Provider value={value}>{children}</TutorDockContext.Provider>
}

/**
 * The dock panel's seed consumer, keyed on `[pendingSeed, currentId]` and
 * **not** a mount effect: `useSessions` bootstraps `current` from IndexedDB
 * asynchronously and `useTutorStream.send` is a silent no-op while
 * `sessionId === null`, so a send on mount is dropped — and an already-open
 * dock does not remount. `takeSeed` clears before the send, so StrictMode's
 * second run finds nothing to send (`useDueCards.ts:19–39`'s order).
 */
export function useDockSeed(
  enabled: boolean,
  currentId: string | null,
  send: (text: string) => void,
): void {
  const { pendingSeed, seedTicket, takeSeed } = useTutorDock()
  useEffect(() => {
    if (!enabled || pendingSeed === null || currentId === null) return
    const text = takeSeed()
    if (text !== null) send(text)
  }, [enabled, pendingSeed, seedTicket, currentId, takeSeed, send])
}
